import DOMPurify from "dompurify";
import type { ThreadMessage, Spokesperson } from "@/hooks/use-post-verdict";

const SAFE_TAGS = ["em", "strong", "p", "br", "a"];
const SAFE_ATTR = ["href", "target", "rel"];
const sanitizeBubble = (s: string) =>
  DOMPurify.sanitize(s ?? "", { ALLOWED_TAGS: SAFE_TAGS, ALLOWED_ATTR: SAFE_ATTR });

interface Props {
  message: ThreadMessage;
  spokesperson: Spokesperson;
}

export function PostVerdictBubble({ message, spokesperson }: Props) {
  if (message.role === "user") {
    return (
      <div
        data-testid="bubble-user"
        style={{
          alignSelf: "flex-end",
          maxWidth: "78%",
          marginLeft: "auto",
          background: "var(--ivory)",
          border: "0.5px solid var(--gold-tint)",
          padding: "14px 18px",
        }}
      >
        <div
          style={{
            fontFamily: '"Playfair Display", serif',
            fontSize: 14,
            color: "var(--charcoal)",
            lineHeight: 1.55,
          }}
        >
          {message.text}
        </div>
        <div
          style={{
            fontFamily: '"DM Mono", monospace',
            fontSize: 9,
            letterSpacing: 1,
            textTransform: "uppercase",
            color: "var(--smoke)",
            marginTop: 6,
            textAlign: "right",
          }}
        >
          {message.time}
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="bubble-spokesperson"
      style={{
        alignSelf: "flex-start",
        maxWidth: "88%",
        marginRight: "auto",
        background: "var(--paper)",
        borderLeft: `2px solid ${spokesperson.color}`,
        padding: "16px 22px",
      }}
    >
      <div style={{ marginBottom: 8 }}>
        <span
          style={{
            fontFamily: '"Playfair Display", serif',
            fontWeight: 600,
            fontSize: 13,
            color: "var(--charcoal)",
          }}
        >
          {spokesperson.name}
        </span>
        <span style={{ color: "var(--smoke)", margin: "0 6px" }}>·</span>
        <span
          style={{
            fontFamily: '"Cormorant Garamond", serif',
            fontStyle: "italic",
            fontSize: 13,
            color: "var(--leather)",
          }}
        >
          {spokesperson.role}
        </span>
      </div>

      {message.isTyping ? (
        <div
          data-testid="typing-indicator"
          style={{ display: "flex", alignItems: "center", gap: 10 }}
        >
          <div style={{ display: "flex", gap: 4 }}>
            {[0, 1, 2].map((i) => (
              <span
                key={i}
                style={{
                  width: 5,
                  height: 5,
                  borderRadius: "50%",
                  background: "var(--smoke)",
                  display: "inline-block",
                  animation: "pvBounce 1.4s infinite ease-in-out",
                  animationDelay: `${i * 0.16}s`,
                }}
              />
            ))}
          </div>
          <span
            style={{
              fontFamily: '"Cormorant Garamond", serif',
              fontStyle: "italic",
              fontSize: 13,
              color: "var(--smoke)",
            }}
          >
            Le porte-parole rédige…
          </span>
          <style>{`@keyframes pvBounce { 0%,80%,100%{transform:translateY(0);opacity:.4} 40%{transform:translateY(-4px);opacity:1} } [data-testid="bubble-spokesperson"] em { font-family: "Cormorant Garamond", serif; font-style: italic; color: var(--leather); }`}</style>
        </div>
      ) : (
        <>
          <div
            data-testid="bubble-text"
            style={{
              fontFamily: '"Playfair Display", serif',
              fontSize: 14,
              color: "var(--charcoal)",
              lineHeight: 1.6,
            }}
            dangerouslySetInnerHTML={{ __html: sanitizeBubble(message.text) }}
          />
          <div
            style={{
              fontFamily: '"Cormorant Garamond", serif',
              fontStyle: "italic",
              fontSize: 13,
              color: "var(--leather)",
              textAlign: "right",
              marginTop: 4,
            }}
          >
            — {spokesperson.name}
          </div>
          <style>{`[data-testid="bubble-spokesperson"] em { font-family: "Cormorant Garamond", serif; font-style: italic; color: var(--leather); }`}</style>
        </>
      )}
    </div>
  );
}

export default PostVerdictBubble;
